// Messages Tab for Client Dashboard - Direct Messaging with Provider
import React, { useState, useEffect } from 'react';
import { MessageCircle, ArrowLeft, User, Shield } from 'lucide-react';
import ConversationList from '../../messaging/ConversationList';
import MessageThread from '../../messaging/MessageThread';
import MessageInput from '../../messaging/MessageInput';
import { useMessaging } from '../../../contexts/MessagingContext';
import { useAuth } from '../../../contexts/AuthContext';

interface ClientMessagesTabProps {
  onBack?: () => void;
  providerId?: string;
  providerName?: string;
  clientId?: string;
  clientName?: string;
}

const ClientMessagesTab: React.FC<ClientMessagesTabProps> = ({ onBack, providerId, providerName, clientId, clientName }) => {
  const { user } = useAuth();
  const {
    conversations,
    activeConversation,
    messages,
    typingIndicator,
    loading,
    selectConversation,
    clearActiveConversation,
    sendMessage,
    createConversation,
    markAsRead,
    setTyping
  } = useMessaging();
  const [startingConversation, setStartingConversation] = useState(false);

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-select conversation: prefer the one with messages (handles duplicates)
  useEffect(() => {
    if (conversations.length >= 1 && !activeConversation) {
      // If multiple conversations exist (e.g., duplicate from index issue),
      // prefer the one with a lastMessage (has actual messages)
      const withMessages = conversations.find(c => c.lastMessage);
      selectConversation((withMessages || conversations[0]).id);
    }
  }, [conversations, activeConversation, selectConversation]);

  // Start a new conversation with the provider
  const handleStartConversation = async () => {
    if (!providerId || !providerName || !clientId || !clientName) return;
    setStartingConversation(true);
    try {
      await createConversation({
        providerId,
        providerName,
        clientId,
        clientName,
      });
    } catch (error) {
      console.error('Failed to start conversation:', error);
    } finally {
      setStartingConversation(false);
    }
  };

  // Handle sending message
  const handleSendMessage = async (content: string) => {
    try {
      await sendMessage(content);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  // Handle back button
  const handleBack = () => {
    if (activeConversation && conversations.length > 1) {
      clearActiveConversation();
    } else if (onBack) {
      onBack();
    }
  };

  // Render header for the messages section
  const renderHeader = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        backgroundColor: '#1e293b',
        borderBottom: '1px solid #334155'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {onBack && (
          <button
            onClick={handleBack}
            aria-label="Go back"
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              color: '#94a3b8',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px'
            }}
          >
            <ArrowLeft size={20} />
          </button>
        )}

        {activeConversation ? (
          <>
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                backgroundColor: '#3b82f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <User size={22} color="#fff" />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#f8fafc'
                }}
              >
                {activeConversation.providerName}
              </h2>
              <p
                style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#94a3b8'
                }}
              >
                Your Provider
              </p>
            </div>
          </>
        ) : (
          <>
            <MessageCircle size={24} color="#3b82f6" />
            <h2
              style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: '#f8fafc'
              }}
            >
              Messages
            </h2>
          </>
        )}
      </div>

      {/* Phone/video call buttons removed — not functional. Video sessions are launched from Appointments. */}
    </div>
  );

  // Render empty state when no conversations
  const renderEmptyState = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 1,
        padding: '40px',
        textAlign: 'center'
      }}
    >
      <div
        style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '20px'
        }}
      >
        <MessageCircle size={40} color="#94a3b8" />
      </div>
      <h3
        style={{
          margin: '0 0 8px 0',
          fontSize: '18px',
          color: '#f8fafc',
          fontWeight: '600'
        }}
      >
        No messages yet
      </h3>
      <p
        style={{
          margin: '0 0 20px 0',
          fontSize: '14px',
          color: '#94a3b8',
          maxWidth: '300px'
        }}
      >
        {providerId
          ? `Send a message to ${providerName || 'your provider'} to get started.`
          : 'Your provider will be able to send you messages here. You can also reach out to them anytime.'}
      </p>

      {providerId && (
        <button
          onClick={handleStartConversation}
          disabled={startingConversation}
          style={{
            background: startingConversation ? '#4b5563' : '#3b82f6',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            color: 'white',
            cursor: startingConversation ? 'not-allowed' : 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <MessageCircle size={18} />
          {startingConversation ? 'Starting...' : `Message ${providerName || 'Your Provider'}`}
        </button>
      )}

      {/* Security Note */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#1e293b',
          borderRadius: '8px',
          color: '#94a3b8',
          fontSize: '13px'
        }}
      >
        <Shield size={16} color="#22c55e" />
        <span>All messages are secure and HIPAA-compliant</span>
      </div>
    </div>
  );

  // Clients typically have one provider — show conversation directly
  // Also handles duplicate conversations (same provider, multiple docs)
  const uniqueProviders = new Set(conversations.map(c => c.providerId));
  const showDirectConversation = uniqueProviders.size <= 1 && conversations.length >= 1;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#0f172a',
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid #334155'
      }}
    >
      {renderHeader()}

      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            color: '#94a3b8'
          }}
        >
          Loading messages...
        </div>
      ) : conversations.length === 0 ? (
        renderEmptyState()
      ) : showDirectConversation || activeConversation ? (
        // Show message thread
        <>
          <MessageThread
            messages={messages}
            currentUserId={clientId || user?.uid || ''}
            currentUserRole="client"
            otherParticipantName={
              activeConversation?.providerName || conversations[0]?.providerName || 'Provider'
            }
            typingIndicator={typingIndicator}
            onMessagesViewed={markAsRead}
          />
          <MessageInput
            onSendMessage={handleSendMessage}
            onTyping={setTyping}
            placeholder="Type a message to your provider..."
          />
        </>
      ) : (
        // Show conversation list (for clients with multiple providers - rare but possible)
        <ConversationList
          conversations={conversations}
          activeConversationId={null}
          userRole="client"
          onSelectConversation={selectConversation}
          loading={loading}
        />
      )}
    </div>
  );
};

export default ClientMessagesTab;
